import { config } from "@repo/config";
import { apiClient } from "@shared/lib/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// Location data for AI report generation (city/region/country)
export interface LocationData {
	city?: string;
	region?: string;
	country?: string;
}

export interface BrandSnapshot {
	id: string;
	data: {
		name?: string | null;
		tagline?: string | null;
		category?: string | null;
		icp?: {
			who?: string | null;
			segments: string[];
		};
		problems_solved: string[];
		features: string[];
		pricing: string[];
		pricing_model?: {
			type:
				| "public"
				| "sales_led"
				| "freemium"
				| "subscription"
				| "usage_based"
				| "unknown";
			indicators: string[];
			sales_ctas: string[];
		};
		integrations: string[];
		differentiators: string[];
		proof_points: string[];
		key_pages: string[];
		sources: {
			[key: string]: string[];
		};
		notes: string[];
		competitors: Competitor[];
	};
	createdAt: string;
}

export interface Competitor {
	id: string;
	url: string;
	name?: string;
	logoUrl?: string;
	createdAt: string;
	updatedAt: string;
}

export interface TechnicalFinding {
	id: string;
	category: string;
	subcategory: string;
	severity: "high" | "medium" | "low";
	status: "red" | "yellow" | "green";
	issueKey: string;
	solutionKey: string;
	explanationKey: string;
	context?: any;
	impactLevel: "high" | "medium" | "low";
	estimatedEffort?: "quick" | "moderate" | "extensive";
	recommendationId?: string;
	createdAt: string;
}

export interface TechnicalRecommendation {
	id: string;
	category: string;
	priority: "high" | "medium" | "low";
	titleKey: string;
	descriptionKey: string;
	implementationKey: string;
	context?: any;
	estimatedImpact?: "high" | "medium" | "low";
	estimatedTime?: "15min" | "1hour" | "1day" | "1week";
	resourceUrls?: string[];
	findings?: TechnicalFinding[];
	createdAt: string;
	updatedAt: string;
}

export interface TechnicalAnalysis {
	id: string;
	reportId: string;
	overallScore: number;
	structureScore: number;
	accessibilityScore: number;
	readabilityScore: number;
	performanceScore: number;
	pagesAnalyzed: number;
	analysisVersion: string;
	findings: TechnicalFinding[];
	recommendations: TechnicalRecommendation[];
	createdAt: string;
	updatedAt: string;
}

export interface GenerateReportResponse {
	success: boolean;
	report: {
		id: string;
		publicToken: string;
		status: string;
		domain: string;
		consistencyScore?: number | null;
		visibilitySummary?: any;
		purchaseId?: string | null;
		snapshot?: BrandSnapshot | null;
		competitors?: Competitor[];
	};
}

export async function generateAiCreditReport(
	domain: string,
	sessionId?: string,
	location?: LocationData,
): Promise<GenerateReportResponse> {
	const headers: Record<string, string> = {};

	// Include PostHog session ID if available
	if (sessionId) {
		headers["X-POSTHOG-SESSION-ID"] = sessionId;
	}

	const requestData: { domain: string; location?: LocationData } = { domain };
	if (location) {
		requestData.location = location;
	}

	const res = await (apiClient as any)["ai-report"].generate.$post(
		{ json: requestData },
		{ headers },
	);

	if (!res.ok) {
		const errorData = await res.json().catch(() => ({}));
		const errorMessage =
			errorData.message || errorData.error || "Failed to generate report";
		throw new Error(errorMessage);
	}

	return res.json();
}

// Key attribute submitted with a public person report request
export interface PersonReportAttribute {
	message: string;
	sentiment: "POSITIVE" | "NEGATIVE" | "NEUTRAL";
}

export interface GeneratePersonReportInput {
	name: string;
	entityRef?: string;
	attributes?: PersonReportAttribute[];
}

export async function generatePersonAiReport(
	input: GeneratePersonReportInput,
	sessionId?: string,
): Promise<GenerateReportResponse> {
	const headers: Record<string, string> = {};

	// Include PostHog session ID if available
	if (sessionId) {
		headers["X-POSTHOG-SESSION-ID"] = sessionId;
	}

	const res = await (apiClient as any)["ai-report"]["generate-person"].$post(
		{ json: input },
		{ headers },
	);

	if (!res.ok) {
		const errorData = await res.json().catch(() => ({}));
		const errorMessage =
			errorData.message || errorData.error || "Failed to generate report";
		throw new Error(errorMessage);
	}

	return res.json();
}

export async function fetchPublicReport(publicToken: string) {
	const res = await (apiClient as any)["ai-report"][":publicToken"].$get({
		param: { publicToken },
	});
	if (!res.ok) throw new Error("Failed to fetch report");
	return res.json();
}

export async function fetchDiscoveryResults(publicToken: string) {
	const res = await (apiClient as any)["ai-report"][
		":publicToken"
	].discovery.$get({ param: { publicToken } });
	if (!res.ok) throw new Error("Failed to fetch discovery results");
	return res.json();
}

/**
 * Fetch the persisted DiscoveryMetric row for this report. Returns `null` on
 * the success channel when no row has been written yet (in-flight reports);
 * callers fall back to live `calculateSummaryStatistics` in that case.
 *
 * The persisted row is the canonical source for mention/SOV/citation totals
 * shown in the report — it is kept in sync with the property's current
 * aliases/competitors by the historical-metrics-recompute Inngest job (10s
 * debounce after an edit). Reading it here means the report and the
 * historical dashboard never disagree.
 */
export async function fetchReportDiscoveryMetric(
	publicToken: string,
): Promise<{ discoveryMetric: any | null } | null> {
	const res = await (apiClient as any)["ai-report"][":publicToken"][
		"discovery-metric"
	].$get({ param: { publicToken } });
	if (!res.ok) {
		if (res.status === 404) return null;
		throw new Error("Failed to fetch report discovery metric");
	}
	return res.json();
}

export async function fetchCompetitorAnalysis(publicToken: string) {
	const res = await (apiClient as any)["ai-report"][":publicToken"][
		"competitor-analysis"
	].$get({ param: { publicToken } });
	if (!res.ok) {
		if (res.status === 404) return null; // No competitor analysis available
		throw new Error("Failed to fetch competitor analysis");
	}
	return res.json();
}

export async function fetchGroundingSearchRecommendations(publicToken: string) {
	const res = await (apiClient as any)["ai-report"][":publicToken"][
		"grounding-search-recommendations"
	].$get({ param: { publicToken } });
	if (!res.ok) {
		if (res.status === 404) return null; // No recommendations available
		throw new Error("Failed to fetch grounding search recommendations");
	}
	return res.json();
}

export async function fetchTechnicalAnalysis(
	publicToken: string,
): Promise<TechnicalAnalysis | null> {
	const res = await (apiClient as any)["ai-report"][":publicToken"][
		"technical-analysis"
	].$get({ param: { publicToken } });
	if (!res.ok) {
		if (res.status === 404) return null; // No technical analysis available
		throw new Error("Failed to fetch technical analysis");
	}
	return res.json();
}

/**
 * Subject types the public demo-report endpoint can be narrowed to. It filters
 * on the property's own `subjectType`, so a category unlock page and the
 * category marketing page ask for CATEGORY and get nothing rather than a set of
 * brand reports that mean nothing to them.
 */
export type DemoReportSubjectType = "BRAND" | "PERSON" | "PRODUCT" | "CATEGORY";

export async function fetchDemoReports(subjectType?: DemoReportSubjectType) {
	const res = await (apiClient as any).public["demo-reports"].$get(
		subjectType ? { query: { subjectType } } : undefined,
	);
	if (!res.ok) throw new Error("Failed to fetch demo reports");
	return res.json();
}

// Purchase API functions
export interface CreateCheckoutLinkRequest {
	reportPublicToken: string;
	email?: string;
	redirectUrl?: string;
}

export interface CreateCheckoutLinkResponse {
	checkoutUrl: string;
}

export interface PurchaseStatusResponse {
	isPurchased: boolean;
	report?: {
		id: string;
		domain: string;
		status: string;
		consistencyScore: number | null;
		completedAt: string | null;
	};
}

export async function createAiReportCheckoutLink(
	data: CreateCheckoutLinkRequest,
): Promise<CreateCheckoutLinkResponse> {
	const res = await (apiClient as any)["ai-report"][
		"create-checkout-link"
	].$post({
		json: data,
	});

	if (!res.ok) {
		const errorData = await res.json().catch(() => ({}));
		throw new Error(JSON.stringify(errorData));
	}

	return res.json();
}

export async function getAiReportPurchaseStatus(
	reportPublicToken: string,
): Promise<PurchaseStatusResponse> {
	const res = await (apiClient as any)["ai-report"][":reportPublicToken"][
		"purchase-status"
	].$get({
		param: { reportPublicToken },
	});

	if (!res.ok) {
		const errorData = await res.json().catch(() => ({}));
		const errorMessage =
			errorData.message ||
			errorData.error ||
			"Failed to get purchase status";
		throw new Error(errorMessage);
	}

	return res.json();
}

// React Query hooks for purchase functionality

export const aiReportQueryKeys = {
	purchaseStatus: (reportPublicToken: string) =>
		["ai-report", "purchase-status", reportPublicToken] as const,
};

export const useCreateAiReportCheckoutLink = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: createAiReportCheckoutLink,
		onSuccess: (_, variables) => {
			// Invalidate the purchase status to check if it's been purchased
			queryClient.invalidateQueries({
				queryKey: aiReportQueryKeys.purchaseStatus(
					variables.reportPublicToken,
				),
			});
		},
	});
};

// Reusable hook for handling the purchase flow
export const usePurchaseFlow = (reportPublicToken: string) => {
	const createCheckoutMutation = useCreateAiReportCheckoutLink();

	const handlePurchase = async () => {
		try {
			const redirectUrl = `${process.env.NEXT_PUBLIC_SITE_URL}/reports/${reportPublicToken}`;
			const { checkoutUrl } = await createCheckoutMutation.mutateAsync({
				reportPublicToken,
				redirectUrl,
			});
			window.location.href = checkoutUrl;
		} catch (error) {
			console.error("Failed to create checkout link:", error);
		}
	};

	return {
		handlePurchase,
		isLoading: createCheckoutMutation.isPending,
		error: createCheckoutMutation.error,
	};
};

export const useAiReportPurchaseStatus = (reportPublicToken: string) => {
	return useQuery({
		queryKey: aiReportQueryKeys.purchaseStatus(reportPublicToken),
		queryFn: () => getAiReportPurchaseStatus(reportPublicToken),
		enabled: !!reportPublicToken,
		// Refetch every 30 seconds to check for purchase completion
		refetchInterval: 30000,
		// Refetch when window is focused to catch purchase completion
		refetchOnWindowFocus: true,
	});
};

/**
 * Gets the AI Report unlock price from config
 */
export function getAiReportPrice() {
	const aiReportPlan = config.payments.plans.aiReportUnlock;
	const price = aiReportPlan.prices?.[0];

	if (!price) {
		throw new Error("AI Report unlock price not configured");
	}

	return price;
}

/**
 * Formats the AI Report price for display
 */
export function formatAiReportPrice(locale = "en-US") {
	const price = getAiReportPrice();

	return Intl.NumberFormat(locale, {
		style: "currency",
		currency: price.currency,
		minimumFractionDigits: 0,
	}).format(price.amount);
}

// Report utility API functions

/**
 * Calculate recommendations summary
 */
export async function calculateRecommendationsSummary(
	brandConsistencyData?: any,
	discoveryStats?: any,
	technicalRecommendations?: any[],
) {
	const res = await (apiClient as any)["ai-reports"].utils[
		"calculate-recommendations-summary"
	].$post({
		json: {
			brandConsistencyData,
			discoveryStats,
			technicalRecommendations,
		},
	});

	if (!res.ok) throw new Error("Failed to calculate recommendations summary");
	return res.json();
}

/**
 * Calculate discovery summary statistics
 */
export async function calculateSummaryStatistics(
	discoveryResults: any[],
	brandSnapshot: any,
	propertyCompetitors: any[] | undefined,
	propertyAliases: string[] | undefined,
	/**
	 * The subject's domain, or null when it owns no website (PERSON / CATEGORY).
	 * Required so a caller can't silently fall back to trusting
	 * `citation.isOurDomain`, which is `true` on every citation of a domainless
	 * property in reports written before that bug was fixed.
	 */
	brandDomain: string | null,
) {
	const res = await (apiClient as any)["ai-reports"].utils[
		"calculate-summary-statistics"
	].$post({
		json: {
			discoveryResults,
			brandSnapshot,
			propertyCompetitors,
			propertyAliases,
			brandDomain,
		},
	});

	if (!res.ok) throw new Error("Failed to calculate summary statistics");
	return res.json();
}

/**
 * Get platform display names mapping
 */
export async function getPlatformDisplayNames(): Promise<
	Record<string, string>
> {
	const res = await (apiClient as any)["public-ai-reports"].utils[
		"platform-display-names"
	].$get();
	if (!res.ok) throw new Error("Failed to fetch platform display names");
	return res.json();
}

/**
 * Get platform logos mapping
 */
export async function getPlatformLogos(): Promise<Record<string, string>> {
	const res = await (apiClient as any)["public-ai-reports"].utils[
		"platform-logos"
	].$get();
	if (!res.ok) throw new Error("Failed to fetch platform logos");
	return res.json();
}

/**
 * Get platform order mapping
 */
export async function getPlatformOrder(): Promise<Record<string, number>> {
	const res = await (apiClient as any)["public-ai-reports"].utils[
		"platform-order"
	].$get();
	if (!res.ok) throw new Error("Failed to fetch platform order");
	return res.json();
}

/**
 * Report generation estimate response
 */
export interface ReportGenerationEstimate {
	success: boolean;
	estimateMinutes: number;
	estimateSeconds: number;
	sampleSize: number;
	displayText: string;
}

/**
 * Fetch the estimated time for report generation based on historical data
 */
export async function fetchReportGenerationEstimate(): Promise<ReportGenerationEstimate> {
	const res = await (apiClient as any).public[
		"report-generation-estimate"
	].$get();
	if (!res.ok) {
		// Return default estimate on error
		return {
			success: false,
			estimateMinutes: 3,
			estimateSeconds: 180,
			sampleSize: 0,
			displayText: "about 3 minutes",
		};
	}
	return res.json();
}

/**
 * Set email notification for when a report is ready
 * @param publicToken The public token of the report
 * @param email The email address to notify
 */
export async function setReportNotifyEmail(
	publicToken: string,
	email: string,
): Promise<{ success: boolean; message: string }> {
	const res = await (apiClient as any)["public-ai-reports"][
		"notify-email"
	].$post({
		json: { publicToken, email },
	});

	if (!res.ok) {
		const errorData = await res.json().catch(() => ({}));
		throw new Error(errorData.error || "Failed to set notification");
	}

	return res.json();
}
